<template>
  <v-card :loading="loading" v-bind="$attrs" v-on="$listeners">
    <v-toolbar flat>
      <v-toolbar-title class="headline"
        >Sales Activity
        <span> (Last {{ selectedDays }} Days)</span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn class="mr-2" small @click="getData(7)" :loading="loading" :disabled="selectedDays == 7">
        Last 7 days
      </v-btn>
      <v-btn class="mr-2" small @click="getData(30)" :loading="loading" :disabled="selectedDays == 30">
        Last 30 days
      </v-btn>
      <v-btn class="mr-2" small @click="getData(60)" :loading="loading" :disabled="selectedDays == 60">
        Last 60 days
      </v-btn>
      <v-btn class="mr-2" small @click="getData(90)" :loading="loading" :disabled="selectedDays == 90">
        Last 90 days
      </v-btn>
    </v-toolbar>
    <v-card-subtitle>
      Graph represents paid and unpaid transactions.
    </v-card-subtitle>
    <v-card-text v-if="!loading">
      <apexchart :height="height ? height : ''" :options="options" :series="series"></apexchart>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters } from "vuex";

export default {
  name: "SalesPayoutCountAndAmountByDateChart",
  components: {},
  props: {
    height: { type: Number, default: 400 }
  },
  data() {
    return {
      loading: false,
      selectedDays: 7,

      options: {
        chart: {
          type: "line",
          zoom: {
            enabled: false
          }
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: function({ dataPointIndex, w }) {
            const data = w.config.customData[dataPointIndex];

            return `
      <div style="
        padding:12px;
        border-radius:8px;
        background:#fff;
        box-shadow:0 2px 8px rgba(0,0,0,0.15);
        font-size:12px;
        min-width:180px;
      ">
        <div style="font-weight:600; margin-bottom:6px;">
          ${new Date(data.date).toLocaleDateString()}
        </div>

        <div style="display:flex; justify-content:space-between; color:#28a745;">
          <span>Paid</span>
          <span>$${(data.paidAmount || 0).toLocaleString()}</span>
        </div>

        <div style="display:flex; justify-content:space-between; color:#28a745;">
          <span>Paid Count</span>
          <span>${data.paidCount || 0}</span>
        </div>

        <div style="display:flex; justify-content:space-between; color:#ffc107;">
          <span>Pending</span>
          <span>$${(data.pendingAmount || 0).toLocaleString()}</span>
        </div>

        <div style="display:flex; justify-content:space-between; color:#ffc107;">
          <span>Pending Count</span>
          <span>${data.pendingCount || 0}</span>
        </div>

        <hr style="margin:6px 0; border:none; border-top:1px solid #eee;" />

        <div style="display:flex; justify-content:space-between; font-weight:600;">
          <span>Total</span>
          <span>$${(data.payoutAmount || 0).toLocaleString()}</span>
        </div>

        <div style="display:flex; justify-content:space-between;">
          <span>Sales</span>
          <span>${data.saleCount || 0}</span>
        </div>
      </div>
    `;
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(value, series) {
            if (series.seriesIndex == 0) {
              let number = parseFloat(value);
              if (isNaN(number)) {
                return value;
              }
              var formatter = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD"
              });
              return formatter.format(number);
            } else {
              return value;
            }
          }
        },
        plotOptions: {
          bar: {
            dataLabels: {
              position: "top"
            }
          }
        },
        xaxis: {
          // tickPlacement: "between",
          type: "datetime",
          labels: {
            showDuplicates: false,
            format: "MM/dd"
          }
        },
        yaxis: [
          {
            forceNiceScale: true,
            labels: {
              formatter: function(value) {
                let number = parseFloat(value);
                if (isNaN(number)) {
                  return value;
                }
                var formatter = new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD"
                });
                return formatter.format(number);
              }
            }
          },
          {
            forceNiceScale: true,
            opposite: true
          }
        ]
      },
      series: [],
      awardVehicle: null
    };
  },
  methods: {
    async initialize() {
      this.loading = true;

      this.awardVehicle = await this.fetchAwardVehicle();
      if (!this.awardVehicle) {
        console.error("Award Vehicle not found");
        this.loading = false;
        return;
      }
      this.loading = false;

      this.getData();
      this.options.dataLabels.formatter = (val, opts) => this.formatShortNumber(val, opts);
    },

    formatBarLabel(value) {
      if (!value || value == "0" || value == 0 || this.selectedDays > 30) {
        return "";
      } else {
        return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
      }
    },
    formatShortNumber(val, opts) {
      if (opts.seriesIndex === 0) {
        if (val >= 1000000) return `${Math.floor(val / 1000000)}M+`;
        if (val >= 1000) return `${Math.floor(val / 1000)}K+`;

        return val.toLocaleString();
      }
      return val;
    },
    getData(days) {
      if (!this.awardVehicle) {
        this.series = [
          { name: "Amount Sold", type: "column", data: [] },
          { name: "Number Sold", type: "line", data: [] }
        ];
        return;
      }

      this.loading = true;

      if (!days) {
        days = 30;
      }
      this.selectedDays = days;

      let dateMoment = moment().subtract(days, "days");

      let dateValue = dateMoment.format("YYYY-MM-DDTHH:mm:ss.SSSZ");

      let searchForm = {};
      searchForm.isEffective = "Active";
      searchForm.participant = { id: this.selectedParticipant.id };
      searchForm.awardVehicle = { id: this.awardVehicle.id };
      searchForm.saleDateAfter = dateValue;

      this.$api
        .post("/api/sales/search/summary/byParticipant", searchForm)
        .then(({ data }) => {
          this.options.customData = data;
          let seriesPayoutAmount = Array.isArray(data) ? data.map(d => ({ x: d.date, y: d.payoutAmount })) : [];
          let seriesCount = Array.isArray(data) ? data.map(d => ({ x: d.date, y: d.saleCount })) : [];
          this.series = [
            { name: "Amount", type: "column", data: Array.isArray(seriesPayoutAmount) ? seriesPayoutAmount : [] },
            { name: "Number Sold", type: "line", data: Array.isArray(seriesCount) ? seriesCount : [] }
          ];
        })
        .catch(err => {
          console.error("Error loading chart data", err);
          this.series = [
            { name: "Amount", type: "column", data: [] },
            { name: "Number Sold", type: "line", data: [] }
          ];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async fetchAwardVehicle() {
      let searchForm = {};
      if (this.$kawasaki.isSalesperson()) {
        searchForm.awardVehicleKey = "SALESPERSON_PAYOUTS";
      } else if (this.$kawasaki.isDealerPrincipalOrDealership()) {
        searchForm.awardVehicleKey = "DEALER_CREDITS";
      } else return null;

      return await this.$api.post("/api/awardVehicles/search", searchForm).then(({ data }) => {
        return data?.content?.[0] || null;
      });
    }
  },
  mounted() {
    this.options.colors = this.$chart.getColors();
    this.initialize();
  },

  computed: {
    ...mapGetters(["selectedParticipant"])
  }
};
</script>
